import { inject, Injectable, NgZone } from '@angular/core';
import { mapObject } from 'src/app/misc/functions';
import { Column, Row, Table } from 'src/app/models/ui/table';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { Aliased, Key } from 'src/app/types/shared';
import { AppDB, GetRepID } from './dexieDB';
import { KundenAddedFelder, KundenControlledFelder as KFelder } from './anlagenDB';
import { FileForPreview } from 'src/app/content/_components/_shared/preview-thumbnails/preview-thumbnail.type';
import { FileObject } from '../base64/base64.service';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';

export type KBezMap = Map<Key<KFelder>, string>;

@Injectable({ providedIn: 'root' })
export class SharedDB {
  private readonly zone = inject(NgZone);

  private readonly db = inject(AppDB);
  //#region User
  async GetUserID() {
    return (await this.db.user.orderBy('ID').firstKey()) as number;
  }

  async GetGroupIDs(BenutzerID?: number) {
    return await this.zone.runOutsideAngular(async () => {
      BenutzerID ??= await this.GetUserID();
      if (!BenutzerID) return [];
      const verknüpfungen = await this.db.sxmbg.where({ BenutzerID }).toArray();
      const groups = await Promise.all(
        verknüpfungen.map(async verknüpfung => {
          const group = await this.db.kbngr.get(verknüpfung.BenutzerGruppeID);
          return group?.DelStatus == 0 ? group.ID : null;
        })
      );
      if (BenutzerID == 16 /* administrator@domain.de */ || BenutzerID == 1 /* internerUser */) groups.push(1);
      return [...new Set(groups.filter(x => x !== null))];
    });
  }
  //#endregion

  async GetKundenBezeichnungColumnNames(): Promise<KBezMap> {
    const kbezng = await this.db.kbezng.filter(x => x.KShow == true).toArray();
    const keys: KBezMap = new Map<Key<KFelder>, string>();

    kbezng.forEach(kbez => {
      let feld = kbez.FeldName;
      if (feld == 'KundenID') feld = 'FremdID';
      keys.set(feld as Key<KFelder>, kbez.KBezeichnung);
    });

    return keys;
  }

  //#region Medien
  async GetMediaByNames(names: string[]): Promise<FileForPreview[]> {
    return await this.zone.runOutsideAngular(async () => {
      const files = await Promise.all(names.map(name => this.GetMediaByName(name)));
      return files.filter(x => !!x);
    });
  }

  async GetMediaByName(name: string): Promise<FileForPreview> {
    if (!name) return null;
    const media = await this.db.media.get(name);
    return media?.file;
  }

  async SaveMedia(pfad: string, files: FileObject[], savedFiles: string, addToName: string): Promise<string> {
    return await this.zone.runOutsideAngular(async () => {
      try {
        const names = await Promise.all(
          files.map(file => {
            const name = `${addToName}$${BackendLocaleDatePipe.timestamp}.${file.extension}`;
            return this.db.media.add({ pfad, name, file: { ...file, name } });
          })
        );
        if (!names.length) return savedFiles ?? null;
        const newFiles = names.join('|');
        if (!savedFiles) return newFiles;
        return `${newFiles}|${savedFiles}`;
      } catch (_) {
        return null;
      }
    });
  }
  async CleanMedia(deletedFiles: string[]) {
    try {
      await this.db.media.where('name').anyOf(deletedFiles).delete();
    } catch (_) {
      return null;
    }
  }
  //#endregion

  //#region Rep
  async GetRepType() {
    const repId = GetRepID();
    if (!repId) return;

    const replika = await this.db.psynst.get(repId);
    if (!replika) return;

    let funktion: string;
    switch (replika.RepFunction) {
      case 0:
        funktion = 'Inspektion';
        break;
      case 1:
        funktion = 'Wartung';
        break;
      case 4:
        funktion = 'Begehung';
        break;
      default:
        funktion = 'None';
        break;
    }
    return funktion;
  }
  //#endregion
}

export function GetDBStatus(isFullDate = true) {
  const Status = isFullDate ? BackendLocaleDatePipe.now : BackendLocaleDatePipe.today;
  const UserName = sessionStorage.getItem('User') ?? 'NoUser_in_Rep';
  return { Status, UserName };
}
//#region KundenBezeichnungen

export function GetKundenBezFor(keys: Key<KFelder>, kbez: KBezMap): string;
export function GetKundenBezFor(keys: Key<KFelder>[], kbez: KBezMap): MappedColumns<KFelder>;
export function GetKundenBezFor(keys: Key<KFelder> | Key<KFelder>[], kbez: KBezMap): string | MappedColumns<KFelder> {
  return Array.isArray(keys) ? keys.flatMap(key => (kbez.has(key) ? [[key, kbez.get(key)]] : [])) : kbez.get(keys);
}

export const KNFields1_20: Array<keyof KundenAddedFelder> = new Array(20).map(
  (_, i) => `KNField${i + 1}` as Key<KundenAddedFelder>
);
const KundenFelder: Key<KFelder>[] = [
  'Bezirk', //  0
  'Stellwerk', //  1
  'Ort', //  2
  'Anlagenklasse', //  3
  'PlanNr', //  4
  'FremdID', //  5
  'Eigentuemer', //  6
  'Verantwortlich', //  7
  'Kostenstelle', //  8
  'Messfile', //  9
  'Richtung', // 10
  'Befahrung', // 11
  // 12 - 31
  ...KNFields1_20,
];

export function kbezInRange([start, end]: [number?, number?], kbez: KBezMap): MappedColumns<KFelder> {
  if (start > 31 || end > 32) return [];
  return GetKundenBezFor(KundenFelder.slice(start, end), kbez);
}
//#endregion

//#region TableFE helpers
export function AsTableColumns(cols: string[]): Column[] {
  return cols.flatMap(id => (id ? [{ id }] : []));
}

export function AsTableFE<T extends object>(rows: T[], cols?: Key<T>[]): Table<T> {
  if (!rows && !cols) return TABLE_EMPTY();
  rows ??= [];
  rows = rows.filter(x => x != null);
  cols ??= Object.keys(rows[0] ?? {}) as Key<T>[];
  return { rows, columns: AsTableColumns(cols) };
}

export type MappedColumns<T extends object, R extends object = Row> = Array<
  (Key<T> & Key<R>) | [Key<T>, Key<R>] | [Key<T>, Key<R>, (value: T[Key<T>]) => R[Key<R>]]
>;

export function AsMappedTableFE<R extends object = Row>() {
  return <T extends object>(rows: T[], cols?: MappedColumns<T, R>): Table<R> => {
    if (!cols) return AsTableFE<R>(rows as unknown as R[]);
    const { keys, mapFn } = GetKeysAndMapFn(cols);

    return {
      rows: rows.filter(x => x != null).map(mapFn),
      columns: AsTableColumns([...keys.values()]),
    };
  };
}

export function AsMappedRows<R extends object = Row>() {
  return <T extends object>(rows: T[], cols?: MappedColumns<T, R>): R[] => {
    if (!cols) return rows as unknown as R[];
    const mapFn = GetKeysAndMapFn(cols).mapFn;
    return rows.filter(x => x != null).map(mapFn);
  };
}

export function AsMappedRow<R extends object = Row>() {
  return <T extends object>(row: T, cols?: MappedColumns<T, R>): R => {
    if (!row || !cols) return row as unknown as R;
    const mapFn = GetKeysAndMapFn(cols).mapFn;
    return mapFn(row);
  };
}

export function AsTableOrNull<T>(table: Table<T>) {
  if (!table?.rows?.length) return null;
  return table;
}

export function AsAlias<T extends object, Alias extends string>(row: T, alias: Alias) {
  return mapObject<T, Aliased<T, Alias>>(row, ([key, value]) => [`${alias}.${key}`, value]);
}

function GetKeysAndMapFn<T extends object, R extends object>(cols: MappedColumns<T, R>) {
  const keys = new Map<Key<R & T>, { name: Key<R & T>; computation?: (value: T[Key<T>]) => R[Key<R>] }>(
    cols.map(col => {
      if (Array.isArray(col)) {
        const [field, name, computation] = col;
        return [field, { name, computation }];
      }
      return [col, { name: col }];
    })
  );
  const mapFn = (row: T) =>
    mapObject<T, R>(row, ([key, value]) => {
      if (!keys.has(key)) return null;
      const { name, computation } = keys.get(key);
      const mapped: unknown = computation?.(value) ?? value;
      return [name, mapped];
    });

  return { keys: new Map(Array.from(keys.entries()).map(([k, v]) => [k, v.name])), mapFn };
}
//#endregion
